import axios from 'axios';
import { API_CONFIG } from './apiConfig';

let installed = false;
const origins = new Set([API_CONFIG.API_BASE_URL, API_CONFIG.SPECIAL_API_BASE_URL, API_CONFIG.MAPS_BASE_URL]
  .map((value) => { try { return new URL(value).origin; } catch { return null; } }).filter(Boolean));

export function mergeCollectionPage(first, next) {
  if (Array.isArray(first) && Array.isArray(next)) return [...first, ...next];
  if (first && next && typeof first === 'object' && typeof next === 'object') {
    const merged = { ...first };
    for (const key of Object.keys(first)) {
      if (Array.isArray(first[key]) && Array.isArray(next[key])) merged[key] = [...first[key], ...next[key]];
    }
    return merged;
  }
  throw new Error('The server returned an invalid collection page. Please retry.');
}

export function continuationUrl(next, original) {
  const base = new URL(original, window.location.href);
  const target = new URL(next, base);
  // Pagination never forwards credentials to a URL supplied by another origin.
  if (target.origin !== base.origin || target.pathname !== base.pathname) throw new Error('Invalid collection continuation.');
  return target.href;
}

function isApi(value) {
  try { return origins.has(new URL(value, window.location.href).origin); } catch { return false; }
}

export function installCollectionPaging() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  axios.interceptors.response.use(async (response) => {
    if (response.config.__collectionPage || !isApi(response.config.url)) return response;
    let next = response.headers['x-collection-next'];
    let data = response.data;
    const seen = new Set();
    while (next) {
      const url = continuationUrl(next, response.config.url);
      if (seen.has(url)) throw new Error('Repeated collection page. Please retry.');
      seen.add(url);
      const page = await axios({ ...response.config, url, params: undefined, __collectionPage: true });
      data = mergeCollectionPage(data, page.data);
      next = page.headers['x-collection-next'];
    }
    response.data = data;
    return response;
  });

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const url = typeof input === 'string' || input instanceof URL ? String(input) : input.url;
    const response = await originalFetch(input, init);
    if (!isApi(url) || !response.ok || !response.headers.get('X-Collection-Next')) return response;
    let data = await response.json();
    let next = response.headers.get('X-Collection-Next');
    const seen = new Set();
    while (next) {
      const pageUrl = continuationUrl(next, url);
      if (seen.has(pageUrl)) throw new Error('Repeated collection page. Please retry.');
      seen.add(pageUrl);
      const pageInput = input instanceof Request ? new Request(pageUrl, input) : pageUrl;
      const page = await originalFetch(pageInput, init);
      if (!page.ok) throw new Error(`Collection loading failed (${page.status}). Please retry.`);
      data = mergeCollectionPage(data, await page.json());
      next = page.headers.get('X-Collection-Next');
    }
    const headers = new Headers(response.headers);
    headers.delete('X-Collection-Next');
    headers.delete('Content-Length');
    headers.delete('Content-Encoding');
    const merged = new Response(JSON.stringify(data), { status: response.status, statusText: response.statusText, headers });
    Object.defineProperty(merged, 'url', { value: response.url });
    return merged;
  };
}
